<script setup>
// SiteUp CRM (2026-09-18): aba "Conversa" do painel lateral do Kanban.
// SOMENTE LEITURA nesta fase (DECISOES.md, item 2): responder acontece na
// caixa de entrada. Nada de endpoint novo — as mensagens vêm do mesmo
// GET /conversations/:id/messages que a caixa de entrada usa.
import { ref, computed, watch, nextTick, onUnmounted } from 'vue';
import { useI18n } from 'vue-i18n';
import { useStore } from 'vuex';
import messageAPI from '../../../../api/inbox/message';
import { stripMarkdown } from 'dashboard/helper/kanbanCardPreview';

const props = defineProps({
  item: {
    type: Object,
    required: true,
  },
});

const emit = defineEmits(['navigateToConversation', 'link-conversation']);

const { t } = useI18n();
const store = useStore();

const messages = ref([]);
const isLoading = ref(false);
const loadFailed = ref(false);
const threadRef = ref(null);
// Troca de card cancela a resposta em voo do card anterior.
let loadToken = 0;
onUnmounted(() => {
  loadToken += 1;
});

const conversation = computed(
  () =>
    props.item?.conversation || props.item?.item_details?.conversation || null
);
const conversationId = computed(() => conversation.value?.id || null);
const conversationDisplayId = computed(
  () =>
    conversation.value?.display_id ||
    props.item?.conversation_display_id ||
    props.item?.item_details?.conversation_id ||
    null
);

const inboxName = computed(() => {
  const inboxId = conversation.value?.inbox_id;
  if (!inboxId) return '';
  const inbox = store.getters['inboxes/getInbox'](inboxId);
  return inbox?.name || '';
});

const statusLabel = computed(() => {
  const status = conversation.value?.status;
  const map = {
    open: t('KANBAN.ITEM_CONVERSATION.STATUS.OPEN'),
    pending: t('KANBAN.ITEM_CONVERSATION.STATUS.PENDING'),
    resolved: t('KANBAN.ITEM_CONVERSATION.STATUS.RESOLVED'),
    snoozed: t('KANBAN.ITEM_CONVERSATION.STATUS.SNOOZED'),
  };
  return map[status] || '';
});

const headerLine = computed(() => {
  const parts = [];
  if (inboxName.value) parts.push(inboxName.value);
  if (conversationDisplayId.value) {
    parts.push(
      t('KANBAN.CONVERSATION_TAB.CONVERSATION_NUMBER', {
        id: conversationDisplayId.value,
      })
    );
  }
  if (statusLabel.value) parts.push(statusLabel.value);
  return parts.join(' · ');
});

const formatTime = seconds =>
  new Date(Number(seconds) * 1000).toLocaleTimeString('pt-BR', {
    hour: '2-digit',
    minute: '2-digit',
  });

const dayKey = seconds => {
  const date = new Date(Number(seconds) * 1000);
  return `${date.getFullYear()}-${date.getMonth()}-${date.getDate()}`;
};

const daySeparator = seconds => {
  const date = new Date(Number(seconds) * 1000);
  const today = new Date();
  const yesterday = new Date();
  yesterday.setDate(today.getDate() - 1);
  if (dayKey(seconds) === dayKey(today.getTime() / 1000)) {
    return t('KANBAN.CONVERSATION_TAB.TODAY');
  }
  if (dayKey(seconds) === dayKey(yesterday.getTime() / 1000)) {
    return t('KANBAN.CONVERSATION_TAB.YESTERDAY');
  }
  return date.toLocaleDateString('pt-BR', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
  });
};

/**
 * message_type: 0 = entrada (contato), 1 = saída (agente ou IA), 2 = atividade
 * do sistema. A aba mostra só a conversa de verdade.
 */
const visibleMessages = computed(() => {
  let lastDay = null;
  return messages.value.map(message => {
    const key = dayKey(message.createdAt);
    const separator = key === lastDay ? '' : daySeparator(message.createdAt);
    lastDay = key;
    return { ...message, separator };
  });
});

const load = async () => {
  // A API de mensagens do produto procura a conversa pelo display_id (o numero
  // que aparece na tela, "#1844"), nao pelo id interno da tabela. Medido em
  // producao: a conversa #1844 da conta 13 tem id interno 18786, e pedir por
  // 18786 devolvia 404 em toda conversa. A aba nunca carregou mensagem nenhuma.
  const id = conversationDisplayId.value;
  loadToken += 1;
  const token = loadToken;
  if (!id) {
    messages.value = [];
    return;
  }
  isLoading.value = true;
  loadFailed.value = false;
  try {
    const response = await messageAPI.getPreviousMessages({
      conversationId: id,
      before: Math.floor(Date.now() / 1000),
    });
    if (token !== loadToken) return;
    const payload = response?.data?.payload || [];
    messages.value = payload
      .filter(
        message => message.message_type === 0 || message.message_type === 1
      )
      .map(message => ({
        id: message.id,
        outgoing: message.message_type === 1,
        // Nota privada é saída só para a equipe: não pode parecer mensagem
        // enviada ao lead.
        isPrivate: message.private === true,
        text: stripMarkdown(message.content || ''),
        hasAttachment: Boolean(message.attachments?.length),
        createdAt: message.created_at,
        time: formatTime(message.created_at),
        sender: message.sender?.name || '',
      }));
    nextTick(() => {
      if (threadRef.value) {
        threadRef.value.scrollTop = threadRef.value.scrollHeight;
      }
    });
  } catch (error) {
    if (token !== loadToken) return;
    loadFailed.value = true;
    messages.value = [];
  } finally {
    if (token === loadToken) isLoading.value = false;
  }
};

watch(conversationId, load, { immediate: true });

const openInInbox = () => {
  if (!conversationDisplayId.value) return;
  emit('navigateToConversation', conversationDisplayId.value);
};
</script>

<template>
  <div
    class="flex h-full min-h-0 flex-col"
    data-testid="kanban-conversation-tab"
  >
    <!-- Card sem conversa vinculada -->
    <div
      v-if="!conversationId"
      class="rounded-xl border border-dashed border-n-weak p-6 text-center"
    >
      <p class="mb-3 text-sm text-n-slate-11">
        {{ t('KANBAN.NO_LINKED_CONVERSATION') }}
      </p>
      <button
        type="button"
        data-testid="kanban-conversation-link"
        class="inline-flex min-h-11 items-center gap-2 rounded-xl border border-n-weak px-3 py-2 text-sm font-medium text-n-slate-12 transition-colors hover:bg-n-alpha-2 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-n-blue-9"
        @click="emit('link-conversation')"
      >
        <span class="i-ph-link size-4" aria-hidden="true" />
        {{ t('KANBAN.CONVERSATION_TAB.LINK_CONVERSATION') }}
      </button>
    </div>

    <template v-else>
      <!-- Faixa: caixa de entrada · conversa #id · status -->
      <div
        class="flex shrink-0 flex-wrap items-center justify-between gap-2 rounded-xl border border-n-weak bg-n-solid-2 px-3 py-2"
      >
        <span class="min-w-0 truncate text-xs text-n-slate-11">
          {{ headerLine }}
        </span>
        <button
          type="button"
          data-testid="kanban-conversation-open-inbox"
          class="shrink-0 rounded-lg px-2 py-1 text-xs font-semibold text-n-blue-11 underline underline-offset-2 hover:bg-n-alpha-2 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-n-blue-9"
          @click="openInInbox"
        >
          {{ t('KANBAN.CONVERSATION_TAB.OPEN_IN_INBOX') }}
        </button>
      </div>

      <div
        v-if="isLoading"
        class="flex flex-1 items-center justify-center py-10"
      >
        <span
          class="size-6 animate-spin rounded-full border-2 border-n-weak border-t-n-blue-9"
          aria-hidden="true"
        />
        <span class="sr-only">{{ t('KANBAN.LOADING_CONVERSATION') }}</span>
      </div>

      <p
        v-else-if="loadFailed"
        role="alert"
        class="mt-4 rounded-xl border border-n-weak p-4 text-center text-sm text-n-slate-11"
      >
        {{ t('KANBAN.CONVERSATION_TAB.LOAD_ERROR') }}
        <button
          type="button"
          class="ml-1 font-semibold text-n-blue-11 underline underline-offset-2"
          @click="load"
        >
          {{ t('KANBAN.CONVERSATION_TAB.RETRY') }}
        </button>
      </p>

      <p
        v-else-if="!visibleMessages.length"
        class="mt-4 rounded-xl border border-dashed border-n-weak p-6 text-center text-sm text-n-slate-11"
      >
        {{ t('KANBAN.CONVERSATION_TAB.EMPTY') }}
      </p>

      <div
        v-else
        ref="threadRef"
        class="mt-3 flex-1 space-y-2 overflow-y-auto pr-1"
      >
        <template v-for="message in visibleMessages" :key="message.id">
          <p
            v-if="message.separator"
            class="my-3 text-center text-[11px] font-medium uppercase tracking-wide text-n-slate-10"
          >
            {{ message.separator }}
          </p>
          <div
            class="flex"
            :class="message.outgoing ? 'justify-end' : 'justify-start'"
          >
            <!-- Tokens n-* nos DOIS temas: bolha de saída com fundo removido
                 já deixou texto branco invisível no tema claro. -->
            <div
              class="max-w-[85%] rounded-xl px-3 py-2 text-sm"
              :class="[
                message.isPrivate
                  ? 'bg-n-amber-3 text-n-slate-12'
                  : message.outgoing
                    ? 'bg-n-blue-3 text-n-slate-12'
                    : 'bg-n-solid-2 text-n-slate-12',
              ]"
            >
              <p
                v-if="message.isPrivate"
                class="m-0 mb-1 flex items-center gap-1 text-[11px] font-semibold text-n-amber-11"
              >
                <span class="i-lucide-lock size-3" aria-hidden="true" />
                {{ $t('CONVERSATION.REPLYBOX.PRIVATE_NOTE') }}
              </p>
              <p
                v-if="message.text"
                class="m-0 whitespace-pre-wrap break-words"
              >
                {{ message.text }}
              </p>
              <p
                v-else-if="message.hasAttachment"
                class="m-0 italic text-n-slate-11"
              >
                {{ t('KANBAN.CONVERSATION_TAB.ATTACHMENT') }}
              </p>
              <span class="mt-1 block text-[11px] text-n-slate-11">
                {{ message.time
                }}<template v-if="message.outgoing && message.sender">
                  · {{ message.sender }}</template
                >
              </span>
            </div>
          </div>
        </template>
      </div>
    </template>
  </div>
</template>
